/**
 * Accessibility Quick Toggle
 * 
 * Compact dropdown for quick accessibility settings in the navigation bar.
 * Provides toggles for High Contrast, Font Size, and Reduce Motion.
 */

import { useState } from "react";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Accessibility, 
  Sun, 
  Moon, 
  Contrast, 
  Type, 
  Zap,
  Settings,
  ChevronRight,
  RotateCcw
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export function AccessibilityQuickToggle({ variant = "icon" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { 
    theme, 
    setTheme,
    highContrast, 
    setHighContrast,
    accessibility, 
    updateAccessibility,
    isDark 
  } = useTheme();

  const fontSizeOptions = [
    { value: "small", label: "Small", size: "14px" },
    { value: "medium", label: "Medium", size: "16px" },
    { value: "large", label: "Large", size: "18px" },
    { value: "xlarge", label: "Extra Large", size: "20px" },
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={variant === "icon" ? "ghost" : "outline"}
          size={variant === "icon" ? "icon" : "default"}
          className={`relative ${
            highContrast 
              ? "text-green-400 border-green-400" 
              : isDark 
                ? "text-slate-400 hover:text-white" 
                : "text-slate-600 hover:text-slate-900"
          }`}
          data-testid="accessibility-toggle"
          aria-label="Accessibility settings"
        >
          <Accessibility className="w-5 h-5" />
          {variant !== "icon" && <span className="ml-2">Accessibility</span>}
          {highContrast && (
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className={`w-80 p-0 ${
          isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200"
        }`}
        align="end"
        data-testid="accessibility-panel"
      >
        {/* Header */}
        <div className={`px-4 py-3 border-b ${isDark ? "border-slate-700" : "border-slate-200"}`}>
          <div className="flex items-center gap-2">
            <Accessibility className="w-5 h-5 text-teal-500" />
            <h3 className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              Accessibility
            </h3>
          </div>
          <p className={`text-xs mt-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            Quick settings for visual accessibility
          </p>
        </div>

        {/* Settings */}
        <div className="p-4 space-y-4">
          {/* Theme Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isDark ? <Moon className="w-4 h-4 text-slate-400" /> : <Sun className="w-4 h-4 text-amber-500" />}
              <Label className={isDark ? "text-slate-200" : "text-slate-700"}>Dark Mode</Label>
            </div>
            <Switch
              checked={isDark}
              onCheckedChange={(checked) => setTheme(checked ? "dark" : "light")}
              data-testid="dark-mode-toggle"
            />
          </div>

          {/* High Contrast Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Contrast className={`w-4 h-4 ${highContrast ? "text-green-400" : "text-slate-400"}`} />
              <div>
                <Label className={isDark ? "text-slate-200" : "text-slate-700"}>High Contrast</Label>
                <p className={`text-xs ${isDark ? "text-slate-500" : "text-slate-400"}`}>WCAG AAA (7:1)</p>
              </div>
            </div>
            <Switch
              checked={highContrast}
              onCheckedChange={setHighContrast}
              data-testid="high-contrast-toggle"
            />
          </div>

          <Separator className={isDark ? "bg-slate-700" : "bg-slate-200"} />

          {/* Font Size */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Type className="w-4 h-4 text-slate-400" />
              <Label className={isDark ? "text-slate-200" : "text-slate-700"}>Text Size</Label>
            </div>
            <Select
              value={accessibility?.fontSize || "medium"}
              onValueChange={(value) => updateAccessibility({ fontSize: value })}
            >
              <SelectTrigger 
                className={isDark ? "bg-slate-800 border-slate-700 text-white" : ""}
                data-testid="font-size-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {fontSizeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label} ({option.size})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reduce Motion Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-slate-400" />
              <div>
                <Label className={isDark ? "text-slate-200" : "text-slate-700"}>Reduce Motion</Label>
                <p className={`text-xs ${isDark ? "text-slate-500" : "text-slate-400"}`}>Minimize animations</p>
              </div>
            </div>
            <Switch
              checked={accessibility?.reduceMotion || false}
              onCheckedChange={(checked) => updateAccessibility({ reduceMotion: checked })}
              data-testid="reduce-motion-toggle"
            />
          </div>

          <Separator className={isDark ? "bg-slate-700" : "bg-slate-200"} />

          {/* Reset to Defaults */}
          <Button
            variant="outline"
            size="sm"
            className={`w-full ${isDark ? "border-slate-600 text-slate-300 hover:bg-slate-700" : ""}`}
            onClick={() => {
              setTheme("light");
              setHighContrast(false);
              updateAccessibility({ fontSize: "medium", reduceMotion: false });
              toast.success("Reset to defaults");
            }}
            data-testid="reset-defaults-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset to Defaults
          </Button>
        </div>

        {/* Footer - Link to Full Settings */}
        <div className={`px-4 py-3 border-t ${isDark ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-slate-50"}`}>
          <Button
            variant="ghost"
            className={`w-full justify-between ${isDark ? "text-slate-300 hover:text-white hover:bg-slate-700" : "text-slate-600 hover:text-slate-900"}`}
            onClick={() => {
              setOpen(false);
              navigate("/settings/accessibility");
            }}
            data-testid="full-settings-link"
          >
            <span className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              All Accessibility Settings
            </span>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default AccessibilityQuickToggle;
